import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { getUserData } from "@/lib/api";
import { ApiQuizQuestion, ContentItem, StrengthWithContent, UserAssessment, FilterOptions, QuizQuestion } from "@/types";
import { useContent } from "@/hooks/use-content";
import { useQuizProgress, useBestScore, usePrefetchQuizData } from "@/hooks/use-quiz";
import { useAntiScraping } from "@/hooks/use-anti-scraping";
import { Navigation } from "@/components/Navigation";
import { DnaBackground } from "@/components/DnaBackground";
import { SearchFilter } from "@/components/SearchFilter";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PlayCircle, FileText, CheckCircle, Video, BookOpen, Loader2, Trophy, TrendingUp, Target, AlertTriangle } from "lucide-react";
import { ProtectedVideoPlayer } from "@/components/ProtectedVideoPlayer";
import { QuizComponent, type LocalQuizQuestion } from "@/components/QuizComponent";
import { Alert, AlertDescription } from "@/components/ui/alert";

const getDomainColor = (domain: string) => {
  switch (domain) {
    case "Strategic Thinking":
      return "bg-primary/10 text-primary border-primary/25";
    case "Executing":
      return "bg-secondary/10 text-brand-green-dark border-secondary/25";
    case "Influencing":
      return "bg-warning/10 text-warning border-warning/25";
    case "Relationship Building":
      return "bg-brand-teal-dark/10 text-brand-teal-dark border-brand-teal-dark/25";
    default:
      return "bg-secondary/20 text-secondary border-secondary/30";
  }
};

const toCamelCase = (str: string) => {
  return str.replace(/[-\s]+(.)/g, (_, c) => c.toUpperCase()).replace(/^(.)/, (_, c) => c.toLowerCase());
};

export default function Assessments() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const [user, setUser] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStrength, setSelectedStrength] = useState<StrengthWithContent | null>(null);
  const [selectedContent, setSelectedContent] = useState<ContentItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [quizScore, setQuizScore] = useState<number | null>(null);
  const [filters, setFilters] = useState<FilterOptions>({
    domain: null,
    status: null,
    sortBy: 'name'
  });
  const navigate = useNavigate();
  const { getAllContents } = useContent();
  const { prefetchBestScore } = usePrefetchQuizData();
  
  // Anti-scraping protection
  const { suspiciousActivity, isBot } = useAntiScraping();

  // Fetch overall quiz progress
  const { data: quizProgress, isLoading: isLoadingProgress } = useQuizProgress(!!user);

  // Fetch best score for selected content
  const { data: bestScoreData } = useBestScore(
    selectedContent?._id || "",
    !!selectedContent?._id
  );

  // Fetch published content using React Query
  const { data: contentsData, isLoading } = useQuery({
    queryKey: ['publishedContents'],
    queryFn: async () => {
      const response = await getAllContents('published');
      if (response.success && response.data) {
        return response.data as ContentItem[];
      }
      return [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!user,
  });

  // Mock assessments (only used for rank display when real data exists)
  const assessments: UserAssessment[] = [];

  useEffect(() => {
    const userData = getUserData();
    if (!userData) {
      navigate("/auth");
    } else {
      setUser(userData);
    }
  }, [navigate]);

  // Compute strengths from fetched content data
  const strengths: StrengthWithContent[] = React.useMemo(() => {
    if (!contentsData) return [];
    
    const strengthsMap = new Map<string, StrengthWithContent>();
    
    contentsData.forEach(content => {
      const strengthIdStr = content.strengthId._id;
      
      if (!strengthsMap.has(strengthIdStr)) {
        // Calculate total quiz questions from all contents of this strength
        const strengthContents = contentsData.filter(
          c => c.strengthId._id === strengthIdStr
        );
        const totalQuizQuestions = strengthContents.reduce(
          (sum, c) => sum + (c.quiz?.length || 0),
          0
        );
        
        strengthsMap.set(strengthIdStr, {
          id: strengthIdStr,
          name: content.strengthId.name,
          nameAr: content.strengthId.nameAr,
          domain: content.strengthId.domain,
          description: content.shortDescription,
          quiz_questions: totalQuizQuestions,
          contents: []
        });
      }
      strengthsMap.get(strengthIdStr)!.contents!.push(content);
    });
    
    return Array.from(strengthsMap.values());
  }, [contentsData]);

  const getUserRank = (strengthId: string) => {
    const assessment = assessments.find(a => a.strength_id === strengthId);
    return assessment?.rank;
  };

  const handleStrengthClick = (strength: StrengthWithContent) => {
    setSelectedStrength(strength);
    
    // Set first content as selected if available
    if (strength.contents && strength.contents.length > 0) {
      setSelectedContent(strength.contents[0]);
      // Prefetch quiz data for better UX
      prefetchBestScore(strength.contents[0]._id);
    } else {
      setSelectedContent(null);
    }
    
    // Load video progress from localStorage (kept for backward compatibility)
    const videoData = localStorage.getItem(`video_progress_${strength.id}`);
    if (videoData) {
      const { progress } = JSON.parse(videoData);
      setVideoProgress(progress);
    } else {
      setVideoProgress(0);
    }
    
    setIsModalOpen(true);
  };

  const handleVideoComplete = () => {
    console.log("Video completed!");
  };

  const handleQuizComplete = (score: number, passed: boolean, attemptData?: any) => {
    setQuizScore(score);
    console.log(`Quiz completed with score: ${score}%, passed: ${passed}`, attemptData);
  };

  // Convert API quiz format to QuizComponent format
  const getQuizQuestions = (): LocalQuizQuestion[] => {
    if (!selectedContent?.quiz || selectedContent.quiz.length === 0) {
      return [];
    }

    return selectedContent.quiz.map((q, index) => {
      // Find the index of the correct answer (A, B, C, D -> 0, 1, 2, 3)
      const correctIndex = q.correct_answer.charCodeAt(0) - 65; // 'A' = 65
      
      return {
        id: `q${index + 1}`,
        question: q.question,
        options: q.options,
        correctAnswer: correctIndex,
        explanation: q.explanation
      };
    });
  };

  const getFilteredAndSortedStrengths = () => {
    let filtered = [...strengths];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(s => 
        s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply domain filter
    if (filters.domain) {
      filtered = filtered.filter(s => s.domain === filters.domain);
    }

    // Apply status filter
    if (filters.status) {
      filtered = filtered.filter(s => {
        const hasRank = !!getUserRank(s.id);
        if (filters.status === 'completed') return hasRank;
        if (filters.status === 'not-started') return !hasRank;
        return false; // in-progress not implemented yet
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (filters.sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'rank':
          const rankA = getUserRank(a.id) || 999;
          const rankB = getUserRank(b.id) || 999;
          return rankA - rankB;
        case 'date':
        case 'progress':
          // Not implemented yet, default to name
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });

    return filtered;
  };

  if (!user) return null;

  if (isLoading || isLoadingProgress) {
    return (
      <div className="min-h-screen">
        <DnaBackground />
        <Navigation user={user} />
        <main className="pt-20 px-4 pb-10 sm:px-6 sm:pt-24 sm:pb-12">
          <div className="container mx-auto max-w-6xl flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-primary" />
              <p className="text-lg text-muted-foreground">{t('common.loading')}</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Use quiz progress data if available, show zeros when no data
  const completedCount = quizProgress?.summary.totalContentPassed || 0;
  const totalCount = quizProgress?.summary.totalContentAttempted || 34;
  const progress = quizProgress?.summary.passRate || 0;
  const filteredStrengths = getFilteredAndSortedStrengths();

  return (
    <div className="min-h-screen overflow-x-hidden" dir={isRtl ? "rtl" : "ltr"}>
      <DnaBackground />
      <Navigation user={user} />
      
      <main className="pt-20 px-4 pb-10 text-start sm:px-6 sm:pt-24 sm:pb-12">
        <div className="container mx-auto max-w-6xl min-w-0">
          <div className="mb-8 animate-fade-in">
            <h1 className="text-3xl font-bold mb-2 sm:text-4xl">{t('assessments.title')}</h1>
            <p className="text-base text-muted-foreground sm:text-xl">
              {t('assessments.subtitle')}
            </p>
          </div>

          {/* Security Alert */}
          {(suspiciousActivity || isBot) && (
            <Alert variant="destructive" className="mb-6">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Security Alert: Suspicious activity detected. Some features may be restricted.
                If you believe this is an error, please contact support.
              </AlertDescription>
            </Alert>
          )}

          <Card className="p-4 mb-8 bg-card border-border animate-slide-up sm:p-6">
            <div className="grid grid-cols-1 gap-4 mb-4 md:grid-cols-3 sm:gap-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Target className="w-5 h-5 text-primary" />
                  <h3 className="text-sm font-medium text-muted-foreground">
                    {t('assessments.yourProgress')}
                  </h3>
                </div>
                <p className="text-3xl font-bold text-primary">{Math.round(progress)}%</p>
                <p className="text-sm text-muted-foreground">
                  {t('assessments.progressDesc', { completed: completedCount, total: totalCount })}
                </p>
              </div>

              {quizProgress && (
                <>
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Trophy className="w-5 h-5 text-accent" />
                      <h3 className="text-sm font-medium text-muted-foreground">
                        {t('assessments.averageScore')}
                      </h3>
                    </div>
                    <p className="text-3xl font-bold text-accent">
                      {Math.round(quizProgress.summary.averageScore)}%
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {quizProgress.summary.totalAttempts} {t('assessments.totalAttempts')}
                    </p>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-5 h-5 text-success" />
                      <h3 className="text-sm font-medium text-muted-foreground">
                        {t('assessments.passRate')}
                      </h3>
                    </div>
                    <p className="text-3xl font-bold text-success">
                      {Math.round(quizProgress.summary.passRate)}%
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {quizProgress.summary.totalContentPassed} {t('assessments.passed')}
                    </p>
                  </div>
                </>
              )}
            </div>
            <Progress value={progress} className="h-3" />
          </Card>

          <SearchFilter
            onSearch={setSearchTerm}
            onFilterChange={setFilters}
            showDomainFilter={true}
            showStatusFilter={true}
            showSortOptions={true}
          />

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 sm:gap-6">
            {filteredStrengths.map((strength, index) => {
              const userRank = getUserRank(strength.id);
              // Get best score from quiz progress
              const contentProgress = quizProgress?.contentProgress.find(
                (cp) => strength.contents?.some(c => c._id === cp.contentId)
              );
              const hasBestScore = contentProgress && contentProgress.bestScore > 0;
              
              return (
                <Card
                  key={strength.id}
                  className="p-6 bg-card border-border hover:bg-card hover:border-primary/50 transition-all duration-300 shadow-card hover:shadow-glow animate-scale-in flex flex-col h-full"
                  style={{ animationDelay: `${index * 0.03}s` }}
                >
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="min-w-0">
                      <h3 className="text-lg font-bold text-foreground mb-1 flex min-w-0 flex-wrap items-center gap-2 sm:text-xl">
                        {userRank && (
                          <span className="text-sm font-bold bg-primary/20 text-primary px-2 py-1 rounded">
                            #{userRank}
                          </span>
                        )}
                        {i18n.language === 'ar' ? (strength.nameAr || t(`strengths.${toCamelCase(strength.name)}`, { defaultValue: strength.name })) : t(`strengths.${toCamelCase(strength.name)}`, { defaultValue: strength.name })}
                      </h3>
                      <Badge className={getDomainColor(strength.domain)}>
                        {t(`domains.${toCamelCase(strength.domain)}`, { defaultValue: strength.domain })}
                      </Badge>
                    </div>
                    {(userRank || hasBestScore) && (
                      <CheckCircle className="w-5 h-5 text-accent" />
                    )}
                  </div>

                  {hasBestScore && (
                    <div className="mb-4 p-3 bg-accent/10 rounded-lg border border-accent/20">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">{t('quizProgress.bestScore', { defaultValue: 'Best Score' })}:</span>
                        <span className="text-lg font-bold text-accent">
                          {contentProgress.bestScore}%
                        </span>
                      </div>
                      {contentProgress.passed && (
                        <Badge className="mt-2 status-success text-xs">
                          ✓ {t('quizProgress.passedBadge', { defaultValue: 'Passed' })}
                        </Badge>
                      )}
                    </div>
                  )}

                  <p className="text-muted-foreground mb-6 leading-relaxed text-sm flex-grow">
                    {strength.description}
                  </p>

                  <div className="flex flex-wrap items-center gap-3 mb-4 text-sm text-muted-foreground sm:gap-4">
                    {strength.contents?.some(c => c.videoUrl) && (
                      <div className="flex items-center gap-2">
                        <PlayCircle className="w-4 h-4 text-primary" />
                        <span>{t('assessments.video')}</span>
                      </div>
                    )}
                    {strength.quiz_questions > 0 && (
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-secondary" />
                        <span>{strength.quiz_questions} {t('assessments.questions')}</span>
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={() => handleStrengthClick(strength)}
                    className={
                      userRank
                        ? "w-full bg-muted hover:bg-muted/80"
                        : "w-full bg-primary hover:bg-primary/90 shadow-glow"
                    }
                  >
                    {userRank ? t('assessments.review') : t('assessments.startLearning')}
                  </Button>
                </Card>
              );
            })}
          </div>
        </div>
      </main>

      {/* Content Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-5xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          {selectedStrength && (
            <>
              <DialogHeader>
                <DialogTitle className="flex flex-col gap-3 text-2xl font-bold sm:flex-row sm:items-center sm:justify-between sm:text-3xl">
                  <span className="min-w-0 break-words">{i18n.language === 'ar' ? (selectedStrength.nameAr || t(`strengths.${toCamelCase(selectedStrength.name)}`, { defaultValue: selectedStrength.name })) : t(`strengths.${toCamelCase(selectedStrength.name)}`, { defaultValue: selectedStrength.name })}</span>
                  {bestScoreData && (
                    <Badge className="bg-accent/20 text-accent text-sm font-normal">
                      {t('assessments.best', { defaultValue: 'Best' })}: {bestScoreData.score}%
                    </Badge>
                  )}
                </DialogTitle>
              </DialogHeader>

              {/* Content Selection - Show if multiple contents */}
              {selectedStrength.contents && selectedStrength.contents.length > 1 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3">{t('assessments.availableContent')}</h3>
                  <div className="grid md:grid-cols-2 gap-3">
                    {selectedStrength.contents.map((content) => (
                      <Card
                        key={content._id}
                        onClick={() => setSelectedContent(content)}
                        className={`p-4 cursor-pointer transition-all duration-300 ${
                          selectedContent?._id === content._id
                            ? "bg-primary/20 border-primary shadow-glow"
                            : "bg-card hover:bg-muted border-border"
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <Badge variant="outline" className="text-xs">
                            {content.strengthId.domain}
                          </Badge>
                          {content.videoUrl && (
                            <PlayCircle className="w-4 h-4 text-primary" />
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {content.shortDescription}
                        </p>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Tabbed Content */}
              <Tabs defaultValue="video" className="w-full">
                <div className="-mx-4 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0">
                <TabsList className="mb-6 grid w-max min-w-full grid-cols-3">
                  <TabsTrigger value="video" className="gap-2">
                    <Video className="w-4 h-4" />
                    {t('assessments.video')}
                  </TabsTrigger>
                  <TabsTrigger value="explanation" className="gap-2">
                    <BookOpen className="w-4 h-4" />
                    {t('assessments.explanation')}
                  </TabsTrigger>
                  <TabsTrigger value="quiz" className="gap-2">
                    <FileText className="w-4 h-4" />
                    {t('assessments.quiz')} {quizScore !== null && `(${quizScore}%)`}
                  </TabsTrigger>
                </TabsList>
                </div>

                <TabsContent value="video" className="space-y-4">
                  {selectedContent?.videoUrl ? (
                    <ProtectedVideoPlayer
                      videoUrl={selectedContent.videoUrl}
                      title={selectedStrength.name}
                      strengthName={selectedStrength.name}
                      onComplete={handleVideoComplete}
                      initialProgress={videoProgress}
                      enableProtection={true}
                    />
                  ) : (
                    <Card className="p-6 text-center bg-card border-border sm:p-12">
                      <PlayCircle className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
                      <h3 className="text-xl font-semibold mb-2">{t('assessments.noVideoAvailable')}</h3>
                      <p className="text-muted-foreground">
                        {t('assessments.noVideoDesc')}
                      </p>
                    </Card>
                  )}
                </TabsContent>

                <TabsContent value="explanation" className="space-y-4">
                  {selectedContent ? (
                    <Card className="p-4 bg-card border-border sm:p-8">
                      <div className="flex flex-col gap-3 mb-6 sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0">
                          <h2 className="text-xl font-bold mb-2 sm:text-2xl">{i18n.language === 'ar' ? (selectedStrength.nameAr || t(`strengths.${toCamelCase(selectedStrength.name)}`, { defaultValue: selectedStrength.name })) : t(`strengths.${toCamelCase(selectedStrength.name)}`, { defaultValue: selectedStrength.name })}</h2>
                          <Badge className={getDomainColor(selectedContent.strengthId.domain)}>
                            {t(`domains.${toCamelCase(selectedContent.strengthId.domain)}`, { defaultValue: selectedContent.strengthId.domain })}
                          </Badge>
                        </div>
                      </div>
                      
                      <div className="prose prose-invert max-w-none">
                        <p className="text-lg text-muted-foreground mb-6">
                          {selectedContent.shortDescription}
                        </p>
                        
                        <div className="whitespace-pre-wrap leading-relaxed text-foreground">
                          {selectedContent.mainContent}
                        </div>
                      </div>
                    </Card>
                  ) : (
                    <Card className="p-6 text-center bg-card border-border sm:p-12">
                      <BookOpen className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
                      <h3 className="text-xl font-semibold mb-2">{t('assessments.noContentAvailable')}</h3>
                      <p className="text-muted-foreground">
                        {selectedStrength.description}
                      </p>
                    </Card>
                  )}
                </TabsContent>

                <TabsContent value="quiz" className="space-y-4">
                  {selectedContent?.quiz && selectedContent.quiz.length > 0 ? (
                    <>
                      {bestScoreData && (
                        <Card className="p-4 bg-muted/50 border-border mb-4">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="min-w-0">
                              <p className="text-sm text-muted-foreground">{t('assessments.previousBestAttempt', { defaultValue: 'Previous Best Attempt' })}</p>
                              <p className="text-2xl font-bold text-accent">
                                {bestScoreData.score}%
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {t('assessments.attemptNumber', { number: bestScoreData.attemptNumber, defaultValue: `Attempt #${bestScoreData.attemptNumber}` })} • {new Date(bestScoreData.completedAt).toLocaleDateString(i18n.language === 'ar' ? 'ar-SA' : 'en-US')}
                              </p>
                            </div>
                            <Trophy className="w-12 h-12 text-accent/50" />
                          </div>
                        </Card>
                      )}
                      <QuizComponent
                        contentId={selectedContent._id}
                        strengthName={selectedStrength.name}
                        questions={getQuizQuestions()}
                        passingScore={70}
                        onComplete={handleQuizComplete}
                      />
                    </>
                  ) : (
                    <Card className="p-6 text-center bg-card border-border sm:p-12">
                      <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
                      <h3 className="text-xl font-semibold mb-2">{t('assessment.noQuestions')}</h3>
                      <p className="text-muted-foreground">
                        {t('assessment.noQuestionsDesc')}
                      </p>
                    </Card>
                  )}
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
